import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { X, Search, CheckCircle2, Circle, ChevronDown, Check } from 'lucide-react';
import { Virtuoso } from 'react-virtuoso';
import { debounce } from 'lodash';
import { cn } from '@/lib/utils';

interface DesignationFilterProps {
    designations: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
    isLoading?: boolean;
}

export const DesignationFilter: React.FC<DesignationFilterProps> = ({
    designations,
    selected,
    onChange,
    isLoading = false,
}) => {
    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const virtuosoRef = useRef<any>(null);

    const filteredDesignations = useMemo(() => {
        return designations.filter((d) =>
            d.toLowerCase().includes(search.toLowerCase())
        );
    }, [designations, search]);

    const handleSearchChange = useMemo(() => 
        debounce((val: string) => {
            setSearch(val);
        }, 300),
    []);

    useEffect(() => {
        return () => {
            handleSearchChange.cancel();
        };
    }, [handleSearchChange]);

    const handleToggle = (designation: string) => {
        if (selected.includes(designation)) {
            onChange(selected.filter((d) => d !== designation));
        } else {
            onChange([...selected, designation]);
        }
    };

    const handleSelectAll = () => {
        onChange(filteredDesignations);
    };

    const handleDeselectAll = () => {
        onChange([]);
    };

    const handleRemoveTag = (designation: string) => {
        onChange(selected.filter((d) => d !== designation));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) {
            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                setOpen(true);
            }
            return;
        }

        if (filteredDesignations.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex((prev) => (prev < filteredDesignations.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case ' ':
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0) {
                    handleToggle(filteredDesignations[focusedIndex]);
                }
                break;
            case 'Escape':
                setOpen(false);
                break;
        }
    };

    useEffect(() => {
        if (focusedIndex >= 0 && virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({ index: focusedIndex, align: 'center' });
        }
    }, [focusedIndex]);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Designations</Label>
                {selected.length > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeselectAll}
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
                    >
                        Clear All
                    </Button>
                )}
            </div>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between h-auto min-h-[40px] px-3 py-2 text-left font-normal"
                        onKeyDown={handleKeyDown}
                    >
                        <div className="flex flex-wrap gap-1.5 overflow-hidden">
                            {selected.length === 0 ? (
                                <span className="text-muted-foreground">Select designations...</span>
                            ) : (
                                <>
                                    <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                        {selected[0]}
                                    </Badge>
                                    {selected.length > 1 && (
                                        <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                            +{selected.length - 1} more
                                        </Badge>
                                    )}
                                </>
                            )}
                        </div>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <div className="flex flex-col">
                        <div className="flex items-center border-b px-3 py-2">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <input
                                className="flex h-8 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                                placeholder="Search designations..."
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center justify-between px-2 py-1 bg-muted/30 border-b">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSelectAll}
                                className="h-7 px-2 text-[11px]"
                            >
                                Select All
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDeselectAll}
                                className="h-7 px-2 text-[11px]"
                            >
                                Deselect All
                            </Button>
                        </div>
                        <div className="h-[200px]">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                                    Loading...
                                </div>
                            ) : filteredDesignations.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                                    No results found
                                </div>
                            ) : (
                                <Virtuoso
                                    ref={virtuosoRef}
                                    style={{ height: '100%' }}
                                    data={filteredDesignations}
                                    itemContent={(index, d) => (
                                        <div
                                            key={d}
                                            className={cn(
                                                "flex items-center space-x-2 px-3 py-2 hover:bg-accent cursor-pointer text-sm",
                                                focusedIndex === index && "bg-accent"
                                            )}
                                            onClick={() => handleToggle(d)}
                                            onMouseEnter={() => setFocusedIndex(index)}
                                        >
                                            <div className={cn(
                                                "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                selected.includes(d) ? "bg-primary text-primary-foreground" : "opacity-50"
                                            )}>
                                                {selected.includes(d) && <Check className="h-3 w-3" />}
                                            </div>
                                            <span className="flex-1 truncate">{d}</span>
                                        </div>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {selected.map((d) => (
                        <Badge key={d} variant="secondary" className="flex items-center gap-1 pl-2 pr-1 h-6 text-[11px] bg-muted text-muted-foreground hover:bg-muted/80 border-none font-medium">
                            {d}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(d);
                                }}
                                className="hover:bg-foreground/10 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
};
